/**
 * Microsoft Clarity Analytics Service
 *
 * Bu servis Microsoft Clarity'yi modern TypeScript ile yönetir
 * ve environment kontrolü sağlar.
 */

import Clarity from '@microsoft/clarity'

export interface ClarityConfig {
  projectId: string
  enableInProduction: boolean
  enableInStaging: boolean
  enableInDevelopment: boolean
  excludeAdminPages: boolean
  stagingProjectId?: string
}

export class ClarityService {
  private static instance: ClarityService
  private isInitialized = false
  private config: ClarityConfig

  constructor(config: ClarityConfig) {
    this.config = config
  }

  public static getInstance(config?: ClarityConfig): ClarityService {
    if (!ClarityService.instance && config) {
      ClarityService.instance = new ClarityService(config)
    }
    return ClarityService.instance
  }

  public initialize(): void {
    if (this.isInitialized) {
      console.warn('Clarity already initialized')
      return
    }

    // Environment kontrolü
    if (!this.shouldInitialize()) {
      return
    }

    if (this.config.excludeAdminPages && this.isAdminPage()) {
      return
    }

    try {
      const projectId = this.getProjectId()
      Clarity.init(projectId)
      this.isInitialized = true

      console.info(`Clarity initialized successfully with project ID: ${projectId}`)

      this.setupAdditionalConfigurations()
    } catch (error) {
      return
    }
  }

  // env check
  private shouldInitialize(): boolean {
    const mode = (import.meta as any).env?.MODE || 'development'

    switch (mode) {
      case 'production':
        return this.config.enableInProduction
      case 'staging':
        return this.config.enableInStaging
      case 'development':
        return this.config.enableInDevelopment
      default:
        return false
    }
  }

  private isAdminPage(): boolean {
    return window.location.pathname.split('/')[1] === 'admin'
  }

  private setupAdditionalConfigurations(): void {
    // env tag'i ekle
    const mode = (import.meta as any).env?.MODE || 'development'
    Clarity.setTag('environment', mode)

    // Country tag'i ekle (eğer mevcut ise)
    const country = (import.meta as any).env?.VITE_COUNTRY || 'unknown'
    Clarity.setTag('country', country)
  }

  private getProjectId(): string {
    const mode = (import.meta as any).env?.MODE || 'development'

    if (mode === 'staging') {
      return this.config.stagingProjectId
    }

    return this.config.projectId
  }

  public sendCustomEvent(eventName: string, properties?: Record<string, any>): void {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized, cannot send custom event')
      return
    }

    try {
      Clarity.event(eventName)
    } catch (error) {
      return
    }
  }

  /**
   * User ID set eder
   */
  public setUserId(userId: string): void {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized, cannot set user ID')
      return
    }

    try {
      Clarity.identify(userId)
    } catch (error) {
      console.error('Failed to set user ID in Clarity:', error)
    }
  }

  public setTag(key: string, value: string | string[]): void {
    if (!this.isInitialized) {
      return
    }

    try {
      Clarity.setTag(key, value)
    } catch (error) {
      return
    }
  }

  /**
   * Session URL'ini alır
   */
  public getSessionUrl(): string | null {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized, cannot get session URL')
      return null
    }

    try {
      // NPM paketinde getSessionUrl metodu henüz mevcut değil
      if (window.clarity && typeof window.clarity === 'function') {
        // Clarity'den session ID alınabilir ancak URL henüz desteklenmiyorasa
        console.info('Session URL functionality not yet available in NPM package')
      }
      return null
    } catch (error) {
      return null
    }
  }

  public stop(): void {
    if (this.isInitialized) {
      this.isInitialized = false
    }
  }

  public get initialized(): boolean {
    return this.isInitialized
  }
}

// Default configuration
export const clarityConfig: ClarityConfig = {
  projectId: 'f4ynid6270', // project ID,
  stagingProjectId: 'obvfxdsh5x', // staging project ID
  enableInProduction: true,
  enableInStaging: true,
  enableInDevelopment: false,
  excludeAdminPages: true
}

// Singleton instance
export const clarityService = ClarityService.getInstance(clarityConfig)
